import React, { useState, useRef, useEffect } from "react";
import type { SystemAlert } from "../../services/dashboard.service";
import SectionCard from "./SectionCard";
import { useNotifications } from "../../hooks/useNotifications";
import { NotificationPanel } from "../notifications/NotificationPanel";

// Known feature-flag keys → human-friendly product names, for the
// "Feature 'x' expires on YYYY-MM-DD" trial-expiry message shape.
const FEATURE_LABELS: Record<string, string> = {
  ai_scribe: "AI Scribe",
  whatsapp_notifications: "WhatsApp Notifications",
  abdm_sync: "ABDM Sync",
  advanced_reports: "Advanced Reports",
};

function featureLabel(key: string): string {
  return FEATURE_LABELS[key] ?? key.split("_").map(w => w[0]?.toUpperCase() + w.slice(1)).join(" ");
}

function friendlyDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

// Rewrites known machine-generated alert phrasing into a plain sentence.
// Anything that doesn't match a known shape passes through unchanged.
function humanize(message: string): string {
  const trialExpiry = message.match(/^Feature '([a-z0-9_]+)' expires on (\d{4}-\d{2}-\d{2})\.?$/i);
  if (trialExpiry) {
    return `Your ${featureLabel(trialExpiry[1])} trial ends ${friendlyDate(trialExpiry[2])}.`;
  }
  return message;
}

type Tone = "warning" | "info" | "success" | "error";

const STYLE: Record<Tone, { bg: string; iconColor: string; icon: React.ReactNode }> = {
  warning: {
    bg: "bg-amber-50 border-amber-100",
    iconColor: "text-amber-500",
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" />
      </svg>
    ),
  },
  info: {
    bg: "bg-blue-50 border-blue-100",
    iconColor: "text-blue-500",
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" />
      </svg>
    ),
  },
  success: {
    bg: "bg-green-50 border-green-100",
    iconColor: "text-green-500",
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8Z" />
      </svg>
    ),
  },
  error: {
    bg: "bg-red-50 border-red-100",
    iconColor: "text-red-500",
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" />
      </svg>
    ),
  },
};

interface Normalised {
  tone: Tone;
  title: string;
  detail?: string;
}

// Normalise the two possible shapes (backend {severity, message} and
// frontend default {level, title, detail}) into a single render-time shape.
function normalise(a: SystemAlert): Normalised {
  const tone: Tone = (a.severity ?? a.level ?? "info") as Tone;
  const message = humanize(a.message ?? "");
  // If backend sent {message: "..."}, split the first sentence as title.
  if (message && !a.title) {
    const dotIdx = message.indexOf(".");
    const title = dotIdx > 0 ? message.slice(0, dotIdx) : message;
    const detail = dotIdx > 0 ? message.slice(dotIdx + 1).trim() : undefined;
    return { tone, title, detail: detail || undefined };
  }
  return { tone, title: a.title ?? message ?? "Update", detail: a.detail };
}

interface Props { alerts: SystemAlert[]; }

export const SystemAlerts: React.FC<Props> = ({ alerts }) => {
  const items = (alerts ?? []).map(normalise);
  const [panelOpen, setPanelOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const {
    notifications, loading, loadingMore, hasMore,
    loadMore, markRead, markAllRead, dismiss,
  } = useNotifications();

  useEffect(() => {
    if (!panelOpen) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setPanelOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [panelOpen]);

  return (
    <SectionCard
      title="Updates"
      action={
        <div className="relative" ref={panelRef}>
          <button
            onClick={() => setPanelOpen(o => !o)}
            className="text-xs font-medium text-slate-500 hover:text-slate-800 flex items-center gap-1"
          >
            View All <span aria-hidden>›</span>
          </button>
          {panelOpen && (
            <div className="absolute right-0 top-full mt-2 z-30">
              <NotificationPanel
                notifications={notifications}
                loading={loading}
                hasMore={hasMore}
                loadingMore={loadingMore}
                onMarkRead={markRead}
                onMarkAllRead={markAllRead}
                onDismiss={dismiss}
                onClose={() => setPanelOpen(false)}
                onLoadMore={loadMore}
              />
            </div>
          )}
        </div>
      }
    >
      {items.length === 0 ? (
        <div className="flex items-start gap-3 rounded-xl border border-green-100 bg-green-50 px-3 py-2.5">
          <span className={`mt-0.5 ${STYLE.success.iconColor}`}>{STYLE.success.icon}</span>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-slate-800">All systems normal</p>
            <p className="text-[11px] text-slate-500 mt-0.5">No active alerts.</p>
          </div>
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((a, i) => {
            const s = STYLE[a.tone] ?? STYLE.info;
            return (
              <li key={i} className={`flex items-start gap-3 rounded-xl border px-3 py-2.5 ${s.bg}`}>
                <span className={`mt-0.5 ${s.iconColor}`}>{s.icon}</span>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-slate-800">{a.title}</p>
                  {a.detail && <p className="text-[11px] text-slate-500 mt-0.5">{a.detail}</p>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </SectionCard>
  );
};

export default SystemAlerts;
